'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'

import { Button } from '../Button'
import { Chip } from '../Chip'
import { SearchIcon } from '../Icon'
import { Spinner } from '../Spinner'
import { TextInput } from '../TextInput'
import {
  actionButtonsStyles,
  actionsRowStyles,
  containerStyles,
  listStyles,
  loadingStyles,
  messageStyles,
} from './CheckboxFilterList.styles'
import type {
  CheckboxFilterListProps,
  CheckboxFilterOption,
} from './CheckboxFilterList.types'
import { CheckboxFilterListRow } from './CheckboxFilterListRow'
import { getSortedOptions } from './utils'

export type { CheckboxFilterListProps, CheckboxFilterOption }

export const CheckboxFilterList = ({
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  autoFocusSearch = false,
  filterMode = 'internal',
  sortSelectedFirst = true,
  preserveOptionOrder = false,
  emptyMessage,
  noMatchMessage,
  isLoading = false,
  colorScheme = 'dark',
  maxListHeight = '194px',
}: CheckboxFilterListProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Native autoFocus can lose the focus race to the element whose click
  // mounted this list. Defer the imperative focus until that click completes,
  // matching TypeaheadSearch's established dropdown autofocus behavior.
  useEffect(() => {
    if (!autoFocusSearch) return
    const timer = setTimeout(() => searchInputRef.current?.focus(), 0)
    return () => clearTimeout(timer)
    // Mount-only: autoFocusSearch is not expected to change after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Content signature - the consumer typically rebuilds the options array on
  // every render, so identity alone would re-sort (and unpin) on every toggle.
  // Includes subtitle/caption, not just value+label: a facility already
  // visible in browse mode can gain a "matched by address" caption once the
  // user searches, with its value/label unchanged - without these in the
  // signature that update would never trigger a re-derive and the cue would
  // silently never appear.
  const optionsSignature = useMemo(
    () =>
      options
        .map(
          (o) =>
            `${o.value}\u0000${o.label}\u0000${o.subtitle ?? ''}\u0000${
              o.caption ?? ''
            }`
        )
        .join('\u0001'),
    [options]
  )

  // Latest props for the snapshot effect below, without retriggering it.
  const optionsRef = useRef(options)
  optionsRef.current = options
  const selectedValuesRef = useRef(selectedValues)
  selectedValuesRef.current = selectedValues

  // Pinned order is a snapshot: selected-first sorting is applied on mount,
  // when the option set itself changes, and when the search clears - NOT on
  // every toggle, so rows don't jump around mid-selection.
  const [sortedOptions, setSortedOptions] = useState<CheckboxFilterOption[]>(
    () => getSortedOptions(options, selectedValues, preserveOptionOrder)
  )
  const prevSearchRef = useRef(searchValue)
  const prevSignatureRef = useRef(optionsSignature)

  useEffect(() => {
    const didSearchClear = prevSearchRef.current !== '' && searchValue === ''
    const didOptionsChange = prevSignatureRef.current !== optionsSignature
    prevSearchRef.current = searchValue
    prevSignatureRef.current = optionsSignature
    if (didSearchClear || didOptionsChange) {
      setSortedOptions(
        getSortedOptions(
          optionsRef.current,
          selectedValuesRef.current,
          preserveOptionOrder
        )
      )
    }
  }, [searchValue, optionsSignature, preserveOptionOrder])

  const baseOptions = sortSelectedFirst ? sortedOptions : options

  const displayedOptions = useMemo(() => {
    if (filterMode === 'external' || searchValue === '') return baseOptions
    const search = searchValue.toLowerCase()
    return baseOptions.filter((option) =>
      option.label.toLowerCase().includes(search)
    )
  }, [baseOptions, filterMode, searchValue])

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const areAllSelected = options.every((o) => selectedSet.has(o.value))
  const selectedCount = selectedValues.length

  const isListEmpty = displayedOptions.length === 0
  // In external mode the consumer pre-filters, so an empty `options` with an
  // active search IS a zero-result search; internally an empty `options`
  // means the dependent/empty state regardless of the search text.
  const shouldShowNoMatch =
    isListEmpty &&
    searchValue !== '' &&
    (filterMode === 'external' || options.length > 0)
  const shouldShowEmpty = isListEmpty && !shouldShowNoMatch

  return (
    <Box {...containerStyles}>
      <TextInput
        autoFocus={autoFocusSearch}
        colorScheme={colorScheme}
        leftIcon={<SearchIcon colorScheme={colorScheme} size={16} />}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        ref={searchInputRef}
        value={searchValue}
        width='100%'
      />

      {/* Dependent empty states ("select a facility first") keep the search
          input but have nothing to bulk-select, so the controls row hides.
          A zero-result search is NOT that state: selections can still exist
          (external mode passes options=[]), so the selected count stays. */}
      {(options.length > 0 || shouldShowNoMatch) && (
        <Box {...actionsRowStyles}>
          {/* No Clear affordance here — the trigger field's clear-all X
              already covers it. */}
          <Box {...actionButtonsStyles}>
            <Button
              colorScheme={colorScheme}
              disabled={areAllSelected}
              onClick={onSelectAll}
              size='sm'
              variant='tertiary'
            >
              Select All
            </Button>
          </Box>
          {selectedCount > 0 && (
            <Chip
              colorScheme={colorScheme}
              label={`${selectedCount} Selected`}
              variant='field'
            />
          )}
        </Box>
      )}

      {isLoading ? (
        <Box {...loadingStyles}>
          <Spinner colorScheme={colorScheme} size='sm' />
        </Box>
      ) : (
        <Box {...listStyles(colorScheme, maxListHeight)}>
          {displayedOptions.map((option) => (
            <CheckboxFilterListRow
              colorScheme={colorScheme}
              isSelected={selectedSet.has(option.value)}
              key={option.value}
              onToggle={onToggle}
              option={option}
            />
          ))}
          {shouldShowNoMatch && noMatchMessage && (
            <Box {...messageStyles(colorScheme)}>{noMatchMessage}</Box>
          )}
          {shouldShowEmpty && emptyMessage && (
            <Box {...messageStyles(colorScheme)}>{emptyMessage}</Box>
          )}
        </Box>
      )}
    </Box>
  )
}
